import type {FhsFormData} from '../types';

import {calculateScore} from './calculateScore';
import type {ScoreResult, ScoringInput} from './types';

// Our `age` field is a string code (`under_30s`, `30s`, ...). The scoring
// engine works in numeric ages, so we pick a representative number per code.
// For codes that straddle a band boundary (e.g. "30s" → could be <35 or 35–49),
// we bias toward the older band per the agreed mapping.
const AGE_CODE_TO_NUMERIC: Record<string, number> = {
  under_30s: 25,
  '30s': 35,
  '40s': 45,
  '50s': 55,
  '60s': 65,
  '70_plus': 75,
};

// Counts excluding the explicit "NONE" sentinel — selecting "None of these"
// in the multi-choice steps means the user has zero, not one.
const countSelections = (values: string[] | undefined): number =>
  (values ?? []).filter((v) => v !== 'NONE').length;

// Currency fields in formData are masked display strings (e.g. `$1,234,567`).
// Strip the mask characters and coerce to a number for scoring. Empty/missing
// or non-numeric inputs collapse to 0 — the scoring functions already handle
// zero by either returning 0 or falling through to their "no signal" branch.
const CURRENCY_MASK_CHARS = /[$,\s]/g;

const toAmount = (raw: string | undefined): number => {
  if (raw === undefined || raw.trim() === '') return 0;

  const parsed = Number(raw.replace(CURRENCY_MASK_CHARS, ''));

  return Number.isFinite(parsed) ? parsed : 0;
};

const toScoringInput = (data: Partial<FhsFormData>): ScoringInput => ({
  age: AGE_CODE_TO_NUMERIC[data.age ?? ''] ?? 0,
  married: data.maritalStatus === 'married',
  hasChildren: data.financialDependents === 'yes',
  annualIncome: toAmount(data.annualIncome),
  monthlyExpenses: toAmount(data.monthlyExpenses),
  monthlyDebtPayments: toAmount(data.monthlyDebtPayments),
  liquidSavings: toAmount(data.cashSavings),
  nonRetirementInvestments: toAmount(data.nonRetirementInvestments),
  retirementAccounts: toAmount(data.retirementAccounts),
  realEstateValue:
    data.realEstateOwnership === 'no' ? 0 : toAmount(data.realEstateValue),
  totalDebt: toAmount(data.outstandingDebt),
  insuranceCount: countSelections(data.insurance),
  estateDocsCount: countSelections(data.estatePlanning),
});

export const scoreFromFormData = (data: Partial<FhsFormData>): ScoreResult =>
  calculateScore(toScoringInput(data));

// UI-facing tier classification — shared by the overall dial color and the
// per-category badges. Same cut-offs as the top-level tier, just labelled
// differently for the per-category context.
export type CategoryTier = 'looking-good' | 'worth-a-look' | 'needs-attention';

export const categoryTierFor = (score: number): CategoryTier => {
  if (score >= 75) return 'looking-good';
  if (score >= 50) return 'worth-a-look';

  return 'needs-attention';
};
